import type { SiteLocation } from "./cpeSite";
import type { ExtenderPopPatchBody } from "./requests/request";

/**
 * The location form of a CPE site: what the inputs hold, how they become
 * the PATCH body, and the geocode answer. Pure, so the rules the backend
 * also enforces (both coordinates or neither, ranges) are tested here.
 */

/** The inputs, as typed. */
export interface SiteLocationDraft {
  locationName: string;
  address: string;
  latitude: string;
  longitude: string;
}

export const draftFromLocation = (location: SiteLocation): SiteLocationDraft => ({
  locationName: location.locationName ?? "",
  address: location.address ?? "",
  latitude: location.latitude !== null ? location.latitude.toFixed(6) : "",
  longitude: location.longitude !== null ? location.longitude.toFixed(6) : "",
});

export interface SiteLocationErrors {
  latitude?: string;
  longitude?: string;
}

/** A decimal number as typed: "19.4326", "-99.1332", "+19". */
const DECIMAL = /^[+-]?(\d+(\.\d*)?|\.\d+)$/;

/** Six decimals is ~10 cm, what the backend keeps. */
const round6 = (value: number): number => Math.round(value * 1e6) / 1e6;

const parseCoordinate = (
  raw: string,
  limit: number,
  name: string
): { value: number | null; error?: string } => {
  const typed = raw.trim();
  if (!typed) return { value: null };
  if (!DECIMAL.test(typed)) return { value: null, error: `${name} must be a number` };
  const value = Number(typed);
  if (Math.abs(value) > limit) {
    return { value: null, error: `${name} must be between -${limit} and ${limit}` };
  }
  return { value: round6(value) };
};

/**
 * The PATCH body for the draft, or the errors that keep it from saving.
 * Empty text clears a field; both coordinates or neither.
 */
export const validateSiteLocation = (
  draft: SiteLocationDraft
): { body: ExtenderPopPatchBody | null; errors: SiteLocationErrors } => {
  const latitude = parseCoordinate(draft.latitude, 90, "Latitude");
  const longitude = parseCoordinate(draft.longitude, 180, "Longitude");
  const errors: SiteLocationErrors = {};
  if (latitude.error) errors.latitude = latitude.error;
  if (longitude.error) errors.longitude = longitude.error;

  const hasLatitude = draft.latitude.trim() !== "";
  const hasLongitude = draft.longitude.trim() !== "";
  if (hasLatitude !== hasLongitude) {
    const both = "Enter both latitude and longitude, or neither";
    if (!hasLatitude) errors.latitude = both;
    if (!hasLongitude) errors.longitude = both;
  }

  if (errors.latitude || errors.longitude) return { body: null, errors };
  return {
    body: {
      locationName: draft.locationName.trim() || null,
      address: draft.address.trim() || null,
      latitude: latitude.value,
      longitude: longitude.value,
    },
    errors,
  };
};

/** The geocode lookup needs at least three characters. */
export const canGeocode = (address: string): boolean => address.trim().length >= 3;

export interface GeocodeResult {
  displayName: string;
  latitude: number;
  longitude: number;
}

/** The matches of a geocode answer; malformed entries are dropped. */
export const shapeGeocodeResults = (raw: unknown): GeocodeResult[] => {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (entry): entry is GeocodeResult =>
      typeof entry?.displayName === "string" &&
      Number.isFinite(entry?.latitude) &&
      Number.isFinite(entry?.longitude)
  );
};

/** A picked match fills the GPS inputs, as they would be typed. */
export const applyGeocodeResult = (
  draft: SiteLocationDraft,
  result: GeocodeResult
): SiteLocationDraft => ({
  ...draft,
  latitude: result.latitude.toFixed(6),
  longitude: result.longitude.toFixed(6),
});

/** The GPS pair as the inline editor shows it: "20.588806, -100.389883". */
export const gpsText = (location: SiteLocation): string =>
  location.latitude !== null && location.longitude !== null
    ? `${location.latitude.toFixed(6)}, ${location.longitude.toFixed(6)}`
    : "";

/**
 * The GPS pair typed in one inline field, as the PATCH body wants it:
 * "lat, lon" or "lat lon" (what a map's "copy coordinates" gives). Empty
 * text clears the point.
 */
export const parseGpsText = (
  text: string
): { body: Pick<ExtenderPopPatchBody, "latitude" | "longitude"> } | { error: string } => {
  const typed = text.trim();
  if (!typed) return { body: { latitude: null, longitude: null } };

  const parts = typed.split(/\s*,\s*|\s+/);
  if (parts.length !== 2) {
    return { error: "Type the GPS as latitude, longitude" };
  }
  const { body, errors } = validateSiteLocation({
    locationName: "",
    address: "",
    latitude: parts[0],
    longitude: parts[1],
  });
  if (!body) return { error: errors.latitude ?? errors.longitude ?? "Invalid GPS" };
  return { body: { latitude: body.latitude, longitude: body.longitude } };
};
